import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Scales, Plus, PaperPlaneTilt, Trash, MagnifyingGlass, X, FilePdf, UserPlus, ClockCounterClockwise, CheckCircle, FileText, Signature, Envelope, ChatCircleDots, ChatText, UserCircle } from '@phosphor-icons/react';
import { useLanguage } from '@/contexts/LanguageContext';
import BackButton from '@/components/BackButton';
import EmployeeSearchSelect from './payroll/EmployeeSearchSelect';
import ScrollShadow from '@/components/ScrollShadow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

export default function InvestigationsAdmin() {
  const { language, direction } = useLanguage();
  const isAr = language === 'ar';
  const T = (a, e) => (isAr ? a : e);

  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState({ charges: [], actions: [] });
  const [showCreate, setShowCreate] = useState(false);
  const [showDecision, setShowDecision] = useState(null);
  const [addPartyInv, setAddPartyInv] = useState(null);
  const [newPartyUserId, setNewPartyUserId] = useState('');
  const [newPartyQuestions, setNewPartyQuestions] = useState('');
  const [newPartyRole, setNewPartyRole] = useState('witness');
  const [newPartyReqSig, setNewPartyReqSig] = useState(true);
  const [addingParty, setAddingParty] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timelineInv, setTimelineInv] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [list, cat] = await Promise.all([
        axios.get(`${API}/investigations`, { withCredentials: true }),
        axios.get(`${API}/investigations/catalog`, { withCredentials: true }),
      ]);
      setItems(list.data);
      setCatalog(cat.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // v1.5.31 — fetch employees list at top-level so the Add-Parties
  // dialog can populate its search select without depending on the
  // CreateDialog sub-tree being mounted.
  useEffect(() => {
    axios.get(`${API}/employees`, { withCredentials: true })
      .then(r => setEmployees(r.data || []))
      .catch(() => {});
  }, []);

  const sendResult = async (id) => {
    if (!window.confirm(T('سيتم إخطار أعضاء لجنة التحقيق للتوقيع الرقمي على القرار. لن يصل الموظفون شيئاً في هذه المرحلة. متابعة؟',
                          'Committee members will be notified to digitally sign the decision. Subject employees will NOT receive anything yet. Continue?'))) return;
    try {
      const r = await axios.post(`${API}/investigations/${id}/send-result`, {}, { withCredentials: true });
      toast.success(T(`تم إخطار ${r.data.committee_notified} من أعضاء اللجنة للتوقيع (${r.data.email_sent} إيميل)`,
                       `${r.data.committee_notified} committee member(s) notified to sign (${r.data.email_sent} email(s))`),
                    { duration: 6000 });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // v1.5.33 — open selection dialog instead of confirm() so admin can
  // pick exactly which parties receive the final decision.
  const [sendResultDlg, setSendResultDlg] = useState(null);      // {inv, selected: Set<uid>, visibleWit: Set<uid>}
  const [sendingResult, setSendingResult] = useState(false);

  const sendToEmployees = (inv) => {
    const preSelected = new Set(
      (inv.employees || [])
        .filter(e => (e.role || '').toLowerCase() !== 'witness' && e.user_id)
        .map(e => e.user_id)
    );
    // Witness statement visibility to parties: default OFF (hidden). Admin
    // opts each witness in on a case-by-case basis. Pre-seed with any
    // previously-saved choice so re-opens are idempotent.
    const preVisibleWit = new Set(inv.visible_witness_uids || []);
    setSendResultDlg({ inv, selected: preSelected, visibleWit: preVisibleWit });
  };

  const confirmSendResult = async () => {
    if (!sendResultDlg) return;
    const { inv, selected, visibleWit } = sendResultDlg;
    const ids = Array.from(selected);
    if (ids.length === 0) {
      return toast.error(T('اختر مستلماً واحداً على الأقل', 'Select at least one recipient'));
    }
    setSendingResult(true);
    try {
      const r = await axios.post(
        `${API}/investigations/${inv.id}/send-to-employees`,
        { user_ids: ids, visible_witness_uids: Array.from(visibleWit || []) },
        { withCredentials: true },
      );
      toast.success(T(`تم إرسال القرار إلى ${r.data.notified} طرف (${r.data.email_sent} إيميل)`,
                       `Decision sent to ${r.data.notified} party (${r.data.email_sent} email(s))`),
                    { duration: 6000 });
      setSendResultDlg(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSendingResult(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm(T('حذف نهائي؟', 'Delete permanently?'))) return;
    await axios.delete(`${API}/investigations/${id}`, { withCredentials: true });
    fetchAll();
  };

  const downloadPdf = (id, sessionNumber) => {
    const url = `${API}/investigations/${id}/pdf`;
    const win = window.open(url, '_blank');
    if (!win) toast.error(T('تم منع النافذة المنبثقة', 'Popup blocked'));
  };

  return (
    <div dir={direction} className="min-h-screen bg-[#F2F0FA]" data-testid="investigations-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-3xl font-semibold text-[#655dc6]">{T('التحقيقات الداخلية', 'Internal Investigations')}</h1>
          <Button onClick={() => setShowCreate(true)} className="bg-[#655dc6] hover:bg-[#544cb0]" data-testid="new-investigation-btn">
            <Plus size={16} className="me-1" /> {T('تحقيق جديد', 'New Investigation')}
          </Button>
        </div>

        <Card className="border border-[#C4BFF0]" data-testid="investigations-card">
        <CardHeader>
          <CardTitle className="text-[#655dc6] flex items-center gap-2">
            <Scales size={22} weight="duotone" />
            {T('قائمة التحقيقات', 'Investigations')}
            <Badge variant="outline">{items.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-[#655dc6]/60">{T('جارٍ التحميل...', 'Loading...')}</p>
           : items.length === 0 ? <p className="text-sm text-[#655dc6]/60 text-center py-6">{T('لا توجد تحقيقات', 'No investigations')}</p>
           : (
            <ScrollShadow minChildWidth={860}>
              <Table className="[&_th]:text-center [&_td]:text-center">
              <TableHeader>
                <TableRow>
                  <TableHead>{T('رقم الجلسة', 'Session #')}</TableHead>
                  <TableHead>{T('الموضوع', 'Subject')}</TableHead>
                  <TableHead>{T('الموظفون', 'Employees')}</TableHead>
                  <TableHead>{T('الحالة', 'Status')}</TableHead>
                  <TableHead>{T('المهلة', 'Deadline')}</TableHead>
                  <TableHead>{T('إجراءات', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(inv => {
                  const submitted = (inv.employees || []).filter(e => e.statement_submitted_at).length;
                  const total = (inv.employees || []).length;
                  const committeeSigned = (inv.committee || []).filter(m => m.signed_at).length;
                  const committeeTotal = (inv.committee || []).length;
                  const allCommitteeSigned = committeeTotal > 0 && committeeSigned === committeeTotal;
                  const decisionSaved = !!inv.decision_text;
                  const notifiedForSign = !!inv.committee_notified_at;
                  return (
                    <TableRow key={inv.id} data-testid={`inv-row-${inv.id}`}>
                      <TableCell className="font-mono text-xs">{inv.session_number}</TableCell>
                      <TableCell className="max-w-[240px] truncate" title={inv.subject}>{inv.subject}</TableCell>
                      <TableCell>{submitted}/{total}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <Badge variant="outline" className="text-[10px] w-fit">{inv.status}</Badge>
                          {decisionSaved && committeeTotal > 0 && (
                            <span className={`text-[10px] ${allCommitteeSigned ? 'text-emerald-700' : 'text-amber-700'}`} data-testid={`sig-progress-${inv.id}`}>
                              {T(`توقيعات: ${committeeSigned}/${committeeTotal}`, `Sigs: ${committeeSigned}/${committeeTotal}`)}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs" dir="ltr">{inv.statement_deadline}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-center flex-wrap">
                          <Button size="sm" variant="ghost" onClick={() => setShowDecision(inv)} className="h-7 text-[#655dc6]" data-testid={`decision-${inv.id}`}>
                            {T('القرار', 'Decision')}
                          </Button>

                          {/* Step 1: notify committee to sign (only when decision saved and not yet notified) */}
                          {decisionSaved && !notifiedForSign && !inv.result_sent_at && (
                            <Button size="sm" variant="ghost" onClick={() => sendResult(inv.id)} className="h-7 text-amber-600" data-testid={`notify-committee-${inv.id}`}>
                              <PaperPlaneTilt size={14} className="me-1" /> {T('إخطار اللجنة للتوقيع', 'Notify Committee')}
                            </Button>
                          )}

                          {/* Step 2: send to employees (only when all committee signed and not yet sent) */}
                          {decisionSaved && allCommitteeSigned && !inv.result_sent_at && (
                            <Button size="sm" variant="ghost" onClick={() => sendToEmployees(inv)} className="h-7 text-emerald-600" data-testid={`send-to-employees-${inv.id}`}>
                              <PaperPlaneTilt size={14} className="me-1" /> {T('إرسال القرار للأطراف', 'Send to Parties')}
                            </Button>
                          )}

                          {/* Waiting-state hint */}
                          {decisionSaved && notifiedForSign && !allCommitteeSigned && !inv.result_sent_at && (
                            <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid={`awaiting-sigs-${inv.id}`}>
                              {T('بانتظار توقيع اللجنة', 'Awaiting committee signatures')}
                            </Badge>
                          )}

                          {inv.result_sent_at && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{T('أُرسلت', 'Sent')}</Badge>}

                          {/* v1.5.31 — Add additional parties/witnesses.
                              Available AFTER the initial creation and until
                              the case is officially closed, so the
                              investigator can attach more people whenever
                              new information surfaces. */}
                          {inv.status !== 'closed' && !inv.result_sent_at && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => { setAddPartyInv(inv); setNewPartyUserId(''); setNewPartyQuestions(''); setNewPartyRole('witness'); setNewPartyReqSig(true); }}
                              className="h-7 text-sky-600"
                              data-testid={`add-party-${inv.id}`}
                              title={T('إضافة أطراف / شهود', 'Add parties / witnesses')}
                            >
                              <UserPlus size={14} className="me-1" />
                              {T('إضافة أطراف', 'Add parties')}
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => setTimelineInv(inv)} className="h-7 text-[#655dc6]" title={T('الخط الزمني', 'Timeline')} data-testid={`timeline-${inv.id}`}>
                            <ClockCounterClockwise size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => downloadPdf(inv.id, inv.session_number)} className="h-7 text-[#655dc6]" title={T('تحميل PDF', 'Download PDF')} data-testid={`pdf-${inv.id}`}>
                            <FilePdf size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => del(inv.id)} className="h-7 text-red-600"><Trash size={14} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </ScrollShadow>
          )}
        </CardContent>
      </Card>

      {showCreate && <CreateDialog catalog={catalog} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchAll(); }} T={T} isAr={isAr} direction={direction} />}
      {showDecision && <DecisionDialog inv={showDecision} catalog={catalog} onClose={() => setShowDecision(null)} onSaved={() => { setShowDecision(null); fetchAll(); }} T={T} isAr={isAr} direction={direction} />}
      {timelineInv && <TimelineDialog inv={timelineInv} onClose={() => setTimelineInv(null)} T={T} isAr={isAr} direction={direction} />}

      {/* v1.5.31 — Add-Parties Dialog */}
      <Dialog open={!!addPartyInv} onOpenChange={(o) => { if (!o) setAddPartyInv(null); }}>
        <DialogContent dir={direction}>
          <DialogHeader>
            <DialogTitle data-testid="add-party-title">
              {T('إضافة طرف / شاهد إلى التحقيق', 'Add party / witness to investigation')}
              {addPartyInv && <span className="text-xs text-muted-foreground ms-2">#{addPartyInv.session_number}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{T('الموظف', 'Employee')}</Label>
              <EmployeeSearchSelect
                employees={employees}
                value={newPartyUserId}
                onChange={setNewPartyUserId}
                language={language}
                direction={direction}
                testId="add-party-emp"
                filterFn={(e) => e.role === 'employee'}
              />
            </div>
            <div>
              <Label>{T('الأسئلة / نقاط الاستفسار', 'Questions / topics')}</Label>
              <Textarea
                value={newPartyQuestions}
                onChange={(e) => setNewPartyQuestions(e.target.value)}
                rows={3}
                placeholder={T('اذكر النقاط المطلوبة من الطرف/الشاهد…', 'List the points you need this person to address…')}
                data-testid="add-party-questions"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="party-role" value="witness"
                       checked={newPartyRole === 'witness'}
                       onChange={() => setNewPartyRole('witness')}
                       data-testid="add-party-role-witness" />
                {T('شاهد', 'Witness')}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="party-role" value="party"
                       checked={newPartyRole === 'party'}
                       onChange={() => setNewPartyRole('party')}
                       data-testid="add-party-role-party" />
                {T('طرف', 'Party')}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer ms-auto">
                <Checkbox checked={newPartyReqSig} onCheckedChange={(v) => setNewPartyReqSig(!!v)} data-testid="add-party-req-sig" />
                {T('يتطلب توقيعاً', 'Requires signature')}
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPartyInv(null)}>{T('إلغاء', 'Cancel')}</Button>
            <Button
              disabled={!newPartyUserId || addingParty}
              onClick={async () => {
                setAddingParty(true);
                try {
                  await axios.post(`${API}/investigations/${addPartyInv.id}/add-parties`, {
                    parties: [{
                      user_id: newPartyUserId,
                      questions: newPartyQuestions,
                      role: newPartyRole,
                      requires_signature: newPartyReqSig,
                    }],
                  }, { withCredentials: true });
                  toast.success(T('تم إرسال الطلب للطرف المضاف', 'Party added and notified'));
                  setAddPartyInv(null);
                  fetchAll();
                } catch (e) {
                  toast.error(e.response?.data?.detail || T('فشلت الإضافة', 'Add failed'));
                } finally {
                  setAddingParty(false);
                }
              }}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="add-party-submit"
            >
              {addingParty ? T('جاري الإرسال…', 'Sending…') : T('إضافة وإرسال', 'Add & notify')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v1.5.33 — Send Result: choose which parties receive the decision */}
      <Dialog open={!!sendResultDlg} onOpenChange={(o) => { if (!o) setSendResultDlg(null); }}>
        <DialogContent dir={direction} className="max-w-[560px]">
          <DialogHeader>
            <DialogTitle data-testid="send-result-title" className="text-[#655dc6]">
              {T('اختيار مستلمي القرار', 'Select decision recipients')}
              {sendResultDlg && <span className="text-xs text-muted-foreground ms-2">#{sendResultDlg.inv.session_number}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            <p className="text-xs text-[#655dc6]/70 mb-2">
              {T('حدّد الأطراف الذين ستُرسل لهم نتيجة التحقيق. الشهود غير مُحدَّدين افتراضياً — يمكنك تعديل الاختيار.',
                 'Tick the parties who should receive the final decision. Witnesses are unchecked by default — you may override.')}
            </p>
            {sendResultDlg && (sendResultDlg.inv.employees || []).map((e) => {
              if (!e.user_id) return null;
              const isWitness = (e.role || '').toLowerCase() === 'witness';
              const checked = sendResultDlg.selected.has(e.user_id);
              const witnessDisclosed = sendResultDlg.visibleWit?.has(e.user_id) || false;
              return (
                <label
                  key={e.user_id}
                  className={`flex items-start gap-2 p-2 rounded border ${checked ? 'border-[#655dc6] bg-[#F5F3FF]' : 'border-[#C4BFF0]/60 bg-white'} cursor-pointer transition`}
                  data-testid={`send-target-row-${e.user_id}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(v) => {
                      setSendResultDlg((prev) => {
                        if (!prev) return prev;
                        const nx = new Set(prev.selected);
                        if (v) nx.add(e.user_id); else nx.delete(e.user_id);
                        return { ...prev, selected: nx };
                      });
                    }}
                    data-testid={`send-target-cb-${e.user_id}`}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#655dc6] break-words">{e.name}</span>
                      {e.employee_number && (
                        <span className="text-[10px] text-[#655dc6]/60 font-mono">(#{e.employee_number})</span>
                      )}
                      {isWitness ? (
                        <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">{T('شاهد', 'Witness')}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-[#655dc6] border-[#655dc6]">{T('طرف', 'Party')}</Badge>
                      )}
                      {e.result_sent_at && (
                        <Badge className="text-[10px] bg-emerald-100 text-emerald-700">{T('أُرسل سابقاً', 'Sent earlier')}</Badge>
                      )}
                    </div>
                    {/* Per-witness disclosure toggle. When OFF (default),
                        the witness's statement is masked in the party PDF —
                        only their name is disclosed with "أدلى بأقواله". */}
                    {isWitness && (
                      <div
                        className="mt-2 ms-0 flex items-start gap-2 rounded border border-amber-200 bg-amber-50/50 p-2"
                        onClick={(ev) => ev.preventDefault()}
                      >
                        <Checkbox
                          checked={witnessDisclosed}
                          onCheckedChange={(v) => {
                            setSendResultDlg((prev) => {
                              if (!prev) return prev;
                              const nx = new Set(prev.visibleWit || []);
                              if (v) nx.add(e.user_id); else nx.delete(e.user_id);
                              return { ...prev, visibleWit: nx };
                            });
                          }}
                          data-testid={`send-witness-visible-cb-${e.user_id}`}
                          className="mt-0.5"
                        />
                        <div className="flex-1 text-[11px] leading-snug">
                          <div className="font-medium text-amber-800">
                            {T('إظهار أقوال هذا الشاهد للأطراف', 'Disclose this witness\u2019s statement to parties')}
                          </div>
                          <div className="text-amber-700/70 text-[10px] mt-0.5">
                            {witnessDisclosed
                              ? T('سيرى الأطراف نص أقواله في ملف PDF', 'Parties will see his statement in the PDF')
                              : T('لن يرى الأطراف نص أقواله — سيظهر اسمه فقط مع ملاحظة "أدلى بأقواله"',
                                  'Parties will only see his name with "statement recorded — not disclosed"')}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendResultDlg(null)}>{T('إلغاء', 'Cancel')}</Button>
            <Button
              onClick={confirmSendResult}
              disabled={sendingResult || !sendResultDlg || sendResultDlg.selected.size === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="send-result-confirm"
            >
              <PaperPlaneTilt size={14} className="me-1" />
              {sendingResult
                ? T('جارٍ الإرسال…', 'Sending…')
                : T(`إرسال إلى ${sendResultDlg?.selected.size || 0}`, `Send to ${sendResultDlg?.selected.size || 0}`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </main>
    </div>
  );
}

function CreateDialog({ catalog, onClose, onCreated, T, isAr, direction }) {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    request_date: new Date().toISOString().slice(0, 10),
    session_date: '',
    investigation_type: '', reporting_source: '', subject: '',
  });
  const [charges, setCharges] = useState([]);
  const [committee, setCommittee] = useState([{ user_id: '', role: 'رئيس' }]);
  const [empQuestions, setEmpQuestions] = useState({});  // {employee_id: "questions text"}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/employees?active_only=true`, { withCredentials: true })
      .then(r => setEmployees(r.data || []))
      .catch(() => {});
  }, []);

  const filtered = employees.filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (e.name || '').toLowerCase().includes(q)
      || (e.name_en || '').toLowerCase().includes(q)
      || (e.employee_number || '').includes(q);
  });

  const submit = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (ids.length === 0) return toast.error(T('اختر موظفاً واحداً على الأقل', 'Pick at least one employee'));
    if (!form.subject.trim()) return toast.error(T('اكتب موضوع التحقيق', 'Write the subject'));
    setSaving(true);
    try {
      const employeesPayload = ids.map(uid => ({
        user_id: uid,
        questions: (empQuestions[uid] || '').trim(),
      }));
      const committeePayload = committee
        .filter(c => c.user_id)
        .map(c => ({ user_id: c.user_id, role: c.role || 'عضو' }));
      const r = await axios.post(`${API}/investigations`, {
        ...form, charges, committee: committeePayload, employees: employeesPayload,
      }, { withCredentials: true });
      const emailNote = r.data.email_sent > 0
        ? T(` — تم إرسال ${r.data.email_sent} إيميل`, ` — ${r.data.email_sent} email(s) sent`)
        : T(' — لم يتم إرسال إيميلات (تحقق من إعدادات SMTP)', ' — no emails sent (check SMTP)');
      toast.success(T(`تم إنشاء التحقيق ${r.data.session_number} — المهلة ${r.data.statement_deadline}`,
                       `Created ${r.data.session_number} — deadline ${r.data.statement_deadline}`) + emailNote,
                    { duration: 6000 });
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent dir={direction} className="max-w-[880px] max-h-[90vh] overflow-y-auto" data-testid="create-inv-dialog">
        <DialogHeader><DialogTitle className="text-[#655dc6]">{T('تحقيق داخلي جديد', 'New Internal Investigation')}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Employee multi-select */}
          <div>
            <Label className="text-sm text-[#655dc6] mb-2">{T('الموظفون محل التحقيق *', 'Employees Under Investigation *')}</Label>
            <div className="relative mb-2">
              <MagnifyingGlass size={14} className="absolute top-2.5 start-2 text-[#655dc6]/50" />
              <Input placeholder={T('ابحث بالاسم أو الرقم الوظيفي...', 'Search by name or employee ID...')}
                     value={search} onChange={e => setSearch(e.target.value)} className="ps-8" data-testid="emp-search"/>
            </div>
            <div className="border border-[#C4BFF0] rounded max-h-[220px] overflow-y-auto">
              {filtered.map(e => (
                <label key={e.id} className="flex items-center gap-3 px-3 py-1.5 hover:bg-[#F2F0FA] cursor-pointer border-b border-[#C4BFF0]/30" data-testid={`emp-opt-${e.id}`}>
                  <Checkbox checked={!!selected[e.id]} onCheckedChange={v => setSelected({...selected, [e.id]: !!v})} />
                  <div className="flex-1 flex justify-between items-center text-sm">
                    <span className="text-[#655dc6]">{e.name} <span className="text-xs text-[#655dc6]/60" dir="ltr">/ {e.name_en || '—'}</span></span>
                    <span className="text-xs text-[#655dc6]/70" dir="ltr">#{e.employee_number || '—'}</span>
                  </div>
                </label>
              ))}
              {filtered.length === 0 && <p className="text-xs text-[#655dc6]/50 p-3 text-center">{T('لا نتائج', 'No results')}</p>}
            </div>
            <p className="text-xs text-[#655dc6]/70 mt-1">
              {T(`محدد: ${Object.values(selected).filter(Boolean).length}`, `Selected: ${Object.values(selected).filter(Boolean).length}`)}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">{T('تاريخ الجلسة', 'Session Date')}</Label><Input type="date" value={form.session_date} onChange={e => setForm({...form, session_date: e.target.value})} data-testid="inv-session-date" /></div>
            <div><Label className="text-xs">{T('مصدر البلاغ', 'Reporting Source')}</Label><Input value={form.reporting_source} onChange={e => setForm({...form, reporting_source: e.target.value})} placeholder={T('مثال: إدارة الموارد البشرية', 'e.g., HR Department')} data-testid="inv-reporting-source" /></div>
          </div>

          <div>
            <Label className="text-xs">{T('موضوع التحقيق *', 'Subject *')}</Label>
            <Textarea rows={3} value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} data-testid="inv-subject" />
          </div>

          <div>
            <Label className="text-sm text-[#655dc6] mb-2">{T('المخالفات والاتهامات', 'Charges & Violations')}</Label>
            <div className="grid grid-cols-2 gap-2">
              {catalog.charges.map(c => (
                <label key={c.id} className="flex items-center gap-2 text-sm text-[#655dc6]" data-testid={`charge-${c.id}`}>
                  <Checkbox checked={charges.includes(c.id)}
                            onCheckedChange={v => setCharges(v ? [...charges, c.id] : charges.filter(x => x !== c.id))} />
                  <span>{c[isAr ? 'ar' : 'en']}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Per-employee directed questions */}
          {Object.keys(selected).filter(k => selected[k]).length > 0 && (
            <div>
              <Label className="text-sm text-[#655dc6] mb-2">
                {T('أسئلة موجّهة لكل موظف (اختياري)', 'Targeted Questions Per Employee (optional)')}
              </Label>
              <p className="text-xs text-[#655dc6]/70 mb-2">
                {T('اكتب أسئلة محددة لكل موظف على حدة. ستظهر له في شاشة تدوين الأقوال.',
                   'Write specific questions for each employee. They will appear in their statement screen.')}
              </p>
              <div className="space-y-2">
                {Object.keys(selected).filter(k => selected[k]).map(uid => {
                  const emp = employees.find(x => x.id === uid);
                  return (
                    <div key={uid} className="border border-[#C4BFF0] rounded p-2 bg-white">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-semibold text-[#655dc6]">{emp?.name || uid}</span>
                        <span className="text-[10px] text-[#655dc6]/60" dir="ltr">#{emp?.employee_number || '—'}</span>
                      </div>
                      <Textarea
                        rows={2}
                        placeholder={T('اكتب الأسئلة الموجهة لهذا الموظف...', 'Write questions for this employee...')}
                        value={empQuestions[uid] || ''}
                        onChange={e => setEmpQuestions({ ...empQuestions, [uid]: e.target.value })}
                        data-testid={`emp-questions-${uid}`}
                        className="text-sm"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div className="flex justify-between items-center mb-2">
              <Label className="text-sm text-[#655dc6]">{T('لجنة التحقيق', 'Investigation Committee')}</Label>
              <Button size="sm" variant="ghost" onClick={() => setCommittee([...committee, { user_id: '', role: 'عضو' }])} className="h-7 text-[#655dc6]" data-testid="add-committee-btn">
                <Plus size={12} /> {T('عضو', 'Member')}
              </Button>
            </div>
            <p className="text-xs text-[#655dc6]/70 mb-2">
              {T('اختر أعضاء اللجنة من الموظفين. سيتم إخطارهم بعد إصدار القرار للتوقيع عليه رقمياً.',
                 'Pick committee members from staff. They will be notified to digitally sign the decision after it\'s issued.')}
            </p>
            {committee.map((m, i) => (
              <div key={i} className="grid grid-cols-[1fr_140px_36px] gap-1 mb-1" data-testid={`committee-row-${i}`}>
                <select
                  value={m.user_id || ''}
                  onChange={e => setCommittee(committee.map((x, j) => i === j ? { ...x, user_id: e.target.value } : x))}
                  className="border border-[#C4BFF0] rounded px-2 py-1.5 text-sm bg-white text-[#222]"
                  data-testid={`committee-select-${i}`}
                >
                  <option value="">{T('— اختر موظفاً —', '— Pick an employee —')}</option>
                  {employees.map(e => (
                    <option key={e.id} value={e.id}>
                      {e.name} {e.employee_number ? `(#${e.employee_number})` : ''}
                    </option>
                  ))}
                </select>
                <select
                  value={m.role || 'عضو'}
                  onChange={e => setCommittee(committee.map((x, j) => i === j ? { ...x, role: e.target.value } : x))}
                  className="border border-[#C4BFF0] rounded px-2 py-1.5 text-sm bg-white text-[#222]"
                  data-testid={`committee-role-${i}`}
                >
                  <option value="رئيس">{T('رئيس', 'Chairman')}</option>
                  <option value="عضو">{T('عضو', 'Member')}</option>
                  <option value="مقرر">{T('مقرر', 'Secretary')}</option>
                </select>
                <Button size="sm" variant="ghost" onClick={() => setCommittee(committee.filter((_, j) => j !== i))} className="text-red-600 h-9"><X size={14} /></Button>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{T('إلغاء', 'Cancel')}</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#655dc6] hover:bg-[#544cb0]" data-testid="submit-inv-btn">
            {saving ? T('جارٍ الحفظ...', 'Saving...') : T('إنشاء وإرسال الإشعارات', 'Create & Notify')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({ inv, catalog, onClose, onSaved, T, isAr, direction }) {
  const [findings, setFindings] = useState(inv.findings || '');
  const [action, setAction] = useState(inv.recommended_action || '');
  const [decisionText, setDecisionText] = useState(inv.decision_text || '');
  const [saving, setSaving] = useState(false);
  // Follow-up composer state (per party index)
  const [fuOpenFor, setFuOpenFor] = useState(null);
  const [fuText, setFuText] = useState('');
  const [fuSending, setFuSending] = useState(false);
  const [localInv, setLocalInv] = useState(inv);

  // v1.5.41 — LOCKED state: once every committee member has signed OR
  // the decision has already been sent to the parties, the decision
  // becomes read-only. Signatures on a modifiable decision would be
  // meaningless. The dialog stays open so admins can still review the
  // record and add follow-up questions — only the findings, action
  // and decision text are frozen.
  const committee = inv.committee || [];
  const allSigned = committee.length > 0 && committee.every(m => m.signed_at);
  const locked = allSigned || !!inv.result_sent_at;

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/investigations/${inv.id}/decision`, {
        findings, recommended_action: action, decision_text: decisionText,
      }, { withCredentials: true });
      toast.success(T('تم الحفظ', 'Saved'));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const submitFollowUp = async (partyIndex) => {
    if (!fuText.trim()) return toast.error(T('اكتب السؤال أولاً', 'Enter the question first'));
    setFuSending(true);
    try {
      await axios.post(
        `${API}/investigations/${inv.id}/parties/${partyIndex}/followup`,
        { question: fuText.trim() },
        { withCredentials: true },
      );
      toast.success(T('تم إرسال السؤال للطرف', 'Follow-up sent'));
      // Optimistically append to local copy so the UI updates without reload.
      const nxt = { ...localInv };
      nxt.employees = (nxt.employees || []).map((e, i) => {
        if (i !== partyIndex) return e;
        const fus = [...(e.follow_ups || []), {
          question: fuText.trim(),
          asked_at: new Date().toISOString(),
          answer: '',
          answered_at: null,
        }];
        return { ...e, follow_ups: fus };
      });
      setLocalInv(nxt);
      setFuText('');
      setFuOpenFor(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || T('فشل الإرسال', 'Failed'));
    } finally {
      setFuSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent dir={direction} className="max-w-[720px] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="shrink-0 px-6 pt-5 pb-3 border-b border-[#C4BFF0]/40">
          <DialogTitle className="text-[#655dc6]">{T(`القرار — ${inv.session_number}`, `Decision — ${inv.session_number}`)}</DialogTitle>
        </DialogHeader>

        {/* Scrollable body — everything between header & footer */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
          {/* Subject (fixed inside body) */}
          <div className="bg-[#F2F0FA] p-3 rounded text-sm text-[#655dc6]/80 break-words">
            <strong>{T('الموضوع:', 'Subject:')}</strong> {inv.subject}
          </div>

          {/* LOCKED banner — shows the exact reason the decision is frozen */}
          {locked && (
            <div
              className="border border-emerald-300 bg-emerald-50 rounded p-3 text-xs text-emerald-800 flex items-start gap-2"
              data-testid="decision-locked-banner"
            >
              <CheckCircle size={16} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">
                  {inv.result_sent_at
                    ? T('القرار مُرسَل ومُغلق', 'Decision sent — locked')
                    : T('اكتملت توقيعات اللجنة — القرار مُغلق', 'Committee signatures complete — decision locked')}
                </div>
                <div className="mt-0.5 text-emerald-700/80 leading-relaxed">
                  {T(
                    'لا يمكن تعديل النتائج أو التوصية أو نص القرار حفاظاً على صحة التوقيعات الموقّعة. لتصحيح أي خطأ يلزم فتح تحقيق جديد.',
                    'Findings, action and decision text can no longer be edited so the collected signatures stay valid. To correct a mistake, open a new investigation.',
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Statements — internal scroll for long text */}
          <div className="border border-[#C4BFF0] rounded overflow-hidden flex flex-col" style={{ maxHeight: '360px' }}>
            <div className="bg-[#655dc6] text-white text-xs font-semibold px-3 py-2 shrink-0">
              {T('أقوال الموظفين', 'Employee Statements')}
            </div>
            <div className="overflow-y-auto flex-1 p-3 space-y-3 bg-white">
              {(localInv.employees || []).map((e, idx) => (
                <div key={`${e.user_id}-${idx}`} className={idx > 0 ? 'pt-3 border-t border-[#C4BFF0]/50' : ''}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-xs font-semibold text-[#655dc6] break-words">
                      {e.name} {e.employee_number ? <span className="text-[#655dc6]/60">(#{e.employee_number})</span> : null} — {e.statement_submitted_at ? T('مقدم', 'Submitted') : T('لم يقدم', 'Pending')}
                    </p>
                    {e.statement_submitted_at && (
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 text-[#655dc6]"
                        onClick={() => { setFuOpenFor(idx); setFuText(''); }}
                        data-testid={`add-followup-${idx}`}
                        title={T('إضافة سؤال إضافي لهذا الطرف', 'Add follow-up question for this party')}
                      >
                        <Plus size={12} className="me-1" />
                        {T('سؤال إضافي', 'Follow-up Q')}
                      </Button>
                    )}
                  </div>
                  {e.statement_text ? (
                    <p className="text-xs mt-1 bg-[#F2F0FA] p-2 rounded text-[#222] leading-relaxed"
                       style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      {e.statement_text}
                    </p>
                  ) : (
                    <p className="text-xs mt-1 text-[#655dc6]/50 italic">{T('لم يتم تقديم الأقوال بعد', 'No statement submitted yet')}</p>
                  )}

                  {/* Existing follow-ups for this party */}
                  {(e.follow_ups || []).length > 0 && (
                    <div className="mt-2 space-y-2">
                      {(e.follow_ups || []).map((fu, fi) => (
                        <div key={fi} className="border-s-2 border-[#655dc6] ps-2 py-1 bg-white">
                          <p className="text-[11px] text-[#655dc6] font-semibold">
                            {T(`س${fi + 1}.`, `Q${fi + 1}.`)} {fu.question}
                          </p>
                          {fu.answer ? (
                            <p className="text-[11px] mt-0.5 text-[#065F46] whitespace-pre-wrap"
                               style={{ overflowWrap: 'anywhere' }}>
                              <strong>{T(`ج${fi + 1}.`, `A${fi + 1}.`)} </strong>{fu.answer}
                            </p>
                          ) : (
                            <span className="text-[10px] text-amber-700 italic">
                              {T('بانتظار إجابة الطرف…', 'Awaiting party reply…')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Composer */}
                  {fuOpenFor === idx && (
                    <div className="mt-2 border border-[#C4BFF0] rounded p-2 bg-[#F5F3FF]">
                      <Textarea
                        rows={2}
                        value={fuText}
                        onChange={(ev) => setFuText(ev.target.value)}
                        placeholder={T('اكتب السؤال الإضافي…', 'Type the follow-up question…')}
                        data-testid={`followup-input-${idx}`}
                      />
                      <div className="flex gap-2 justify-end mt-2">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => { setFuOpenFor(null); setFuText(''); }}
                          data-testid={`followup-cancel-${idx}`}
                        >
                          {T('إلغاء', 'Cancel')}
                        </Button>
                        <Button
                          size="sm"
                          disabled={fuSending || !fuText.trim()}
                          onClick={() => submitFollowUp(idx)}
                          className="bg-[#655dc6] hover:bg-[#544cb0] text-white"
                          data-testid={`followup-send-${idx}`}
                        >
                          {fuSending ? T('جارٍ الإرسال…', 'Sending…') : T('إرسال للطرف', 'Send')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">{T('نتائج اللجنة', 'Findings')}</Label>
            <Textarea rows={3} value={findings} onChange={e => setFindings(e.target.value)}
                      disabled={locked} data-testid="findings-input"
                      className={locked ? 'opacity-90 cursor-not-allowed bg-gray-50' : ''}/>
          </div>
          <div>
            <Label className="text-xs">{T('التوصية', 'Recommended Action')}</Label>
            <div className={`grid grid-cols-2 gap-1 text-sm ${locked ? 'opacity-70 pointer-events-none' : ''}`}>
              {catalog.actions.map(a => (
                <label key={a.id} className="flex items-center gap-2">
                  <input type="radio" name="action" checked={action === a.id}
                         onChange={() => setAction(a.id)} disabled={locked}
                         data-testid={`action-${a.id}`}/>
                  <span className="text-[#655dc6]">{a[isAr ? 'ar' : 'en']}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">{T('نص القرار', 'Decision Text')}</Label>
            <Textarea rows={4} value={decisionText} onChange={e => setDecisionText(e.target.value)}
                      disabled={locked} data-testid="decision-text"
                      className={locked ? 'opacity-90 cursor-not-allowed bg-gray-50' : ''}/>
          </div>
        </div>

        <DialogFooter className="shrink-0 px-6 py-4 border-t border-[#C4BFF0]/40 bg-white">
          <Button variant="outline" onClick={onClose}>{T('إغلاق', 'Close')}</Button>
          {!locked && (
            <Button onClick={save} disabled={saving}
                    className="bg-[#655dc6] hover:bg-[#544cb0]"
                    data-testid="decision-save-btn">
              {T('حفظ', 'Save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// TimelineDialog — chronological audit trail of an investigation.
// Icons + timestamps + Arabic/English titles per event.
// ─────────────────────────────────────────────────────────────────────────
const KIND_META = {
  created:            { Icon: FileText,              tint: '#655dc6', bg: '#F5F3FF' },
  party_added:        { Icon: UserPlus,              tint: '#0EA5E9', bg: '#F0F9FF' },
  statement:          { Icon: ChatText,              tint: '#0EA5E9', bg: '#F0F9FF' },
  followup_asked:     { Icon: ChatCircleDots,        tint: '#F59E0B', bg: '#FEF3C7' },
  followup_answered:  { Icon: CheckCircle,           tint: '#059669', bg: '#ECFDF5' },
  decision_saved:     { Icon: Scales,                tint: '#7C3AED', bg: '#F5F3FF' },
  committee_notified: { Icon: Envelope,              tint: '#F59E0B', bg: '#FEF3C7' },
  committee_signed:   { Icon: Signature,             tint: '#059669', bg: '#ECFDF5' },
  result_sent_party:  { Icon: PaperPlaneTilt,        tint: '#059669', bg: '#ECFDF5' },
  result_sent_global: { Icon: PaperPlaneTilt,        tint: '#655dc6', bg: '#F5F3FF' },
};

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const opt = { timeZone: 'Asia/Riyadh', year: 'numeric', month: '2-digit',
                  day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false };
    const parts = new Intl.DateTimeFormat('en-GB', opt).formatToParts(d);
    const g = (t) => (parts.find(p => p.type === t) || {}).value;
    return `${g('year')}-${g('month')}-${g('day')} ${g('hour')}:${g('minute')}`;
  } catch {
    return iso;
  }
}

function TimelineDialog({ inv, onClose, T, isAr, direction }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!inv) return;
    setLoading(true);
    axios.get(`${API}/investigations/${inv.id}/timeline`, { withCredentials: true })
      .then(r => setEvents(r.data.events || []))
      .catch(e => setError(e.response?.data?.detail || 'Failed'))
      .finally(() => setLoading(false));
  }, [inv]);

  return (
    <Dialog open={!!inv} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent dir={direction} className="max-w-[720px] max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="shrink-0 px-6 pt-5 pb-3 border-b border-[#C4BFF0]/40 bg-[#F5F3FF]">
          <DialogTitle data-testid="timeline-title" className="text-[#655dc6] flex items-center gap-2">
            <ClockCounterClockwise size={20} weight="duotone" />
            {T('الخط الزمني للتحقيق', 'Investigation Timeline')}
            {inv && <span className="text-xs font-mono text-[#655dc6]/60 ms-2">#{inv.session_number}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 bg-white">
          {loading && <p className="text-sm text-[#655dc6]/60 text-center py-8">{T('جارٍ التحميل…', 'Loading…')}</p>}
          {error && <p className="text-sm text-red-600 text-center py-4">{error}</p>}
          {!loading && !error && events && events.length === 0 && (
            <p className="text-sm text-[#655dc6]/60 text-center py-8">{T('لا توجد أحداث لعرضها', 'No events to show')}</p>
          )}
          {!loading && !error && events && events.length > 0 && (
            <ol className="relative ms-4" data-testid="timeline-list">
              <div className="absolute top-2 bottom-2 w-0.5 bg-gradient-to-b from-[#C4BFF0] to-[#655dc6]/30" style={isAr ? { right: 16 } : { left: 16 }} />
              {events.map((ev, i) => {
                const meta = KIND_META[ev.kind] || { Icon: FileText, tint: '#655dc6', bg: '#F5F3FF' };
                const Ico = meta.Icon;
                return (
                  <li key={i} className="relative ps-12 pe-2 pb-6" data-testid={`timeline-event-${i}`}>
                    <div
                      className="absolute top-0 flex items-center justify-center w-8 h-8 rounded-full border-2 border-white shadow"
                      style={{ background: meta.bg, color: meta.tint, ...(isAr ? { right: 4 } : { left: 4 }) }}
                    >
                      <Ico size={16} weight="duotone" />
                    </div>
                    <div className="rounded-lg border border-[#C4BFF0]/60 bg-white p-3 hover:border-[#655dc6] transition">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-sm font-semibold" style={{ color: meta.tint }}>
                          {isAr ? ev.title_ar : ev.title_en}
                        </p>
                        <span className="text-[11px] font-mono text-[#655dc6]/70 whitespace-nowrap" dir="ltr">
                          {fmtDateTime(ev.at)}
                        </span>
                      </div>
                      {ev.actor && (
                        <p className="mt-1 text-xs text-[#655dc6]/70 flex items-center gap-1">
                          <UserCircle size={12} /> {ev.actor}
                        </p>
                      )}
                      {ev.meta?.question && (
                        <p className="mt-2 text-xs bg-[#FEF3C7] border-s-2 border-amber-400 rounded p-2 whitespace-pre-wrap"
                           style={{ overflowWrap: 'anywhere' }}>
                          <strong>{T('السؤال: ', 'Question: ')}</strong>{ev.meta.question}
                        </p>
                      )}
                      {ev.meta?.answer && (
                        <p className="mt-2 text-xs bg-[#ECFDF5] border-s-2 border-emerald-400 rounded p-2 whitespace-pre-wrap text-[#065F46]"
                           style={{ overflowWrap: 'anywhere' }}>
                          <strong>{T('الإجابة: ', 'Answer: ')}</strong>{ev.meta.answer}
                        </p>
                      )}
                      {ev.meta?.role && (
                        <Badge variant="outline" className="mt-2 text-[10px] text-[#655dc6] border-[#655dc6]">
                          {ev.meta.role}
                        </Badge>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <DialogFooter className="shrink-0 px-6 py-4 border-t border-[#C4BFF0]/40 bg-white">
          <Button variant="outline" onClick={onClose} data-testid="timeline-close">{T('إغلاق', 'Close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
